/**
 * LoginForm - Форма авторизації
 *
 * Використовує UserManager для авторизації користувача
 * Підтримує кастомізацію полів та обробку помилок
 */

import { Button } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'

import type { IAppConfig } from '@/Core/Contracts/Configs/IAppConfig'
import { userManager } from '@/Core/Managers'
import { configManager } from '@/Core/Managers/Config'
import { Form, FormField } from '@/Core/UI/Form'
import type { LoginFormProps } from '../Auth.types'

import styles from './LoginForm.module.css'

/**
 * Компонент форми авторизації
 *
 * @example
 * ```tsx
 * <LoginForm
 *   forgotPasswordPageUrl="/public/forgot-password"
 *   requiredRoles={['ROLE_ADMIN', 'ROLE_MANAGER']}
 *   onError={(title, message) => notification.error({ message: title, description: message })}
 * />
 * ```
 */
export const LoginForm: React.FC<LoginFormProps> = observer(
    ({
        onSuccess,
        forgotPasswordPageUrl,
        loginFieldName = 'phone',
        loginFieldLabel,
        requiredRoles = [],
        onError,
        authStrategy,
        authStrategyOptions,
        additionalFields,
        transformLoginData,
        loginFieldProps = {},
        passwordFieldProps = {},
    }) => {
        const navigate = useNavigate()
        const [searchParams] = useSearchParams()
        const { t } = useTranslation()
        const [isLoading, setIsLoading] = useState(false)
        const [error, setError] = useState<string | null>(null)

        // Отримуємо конфігурацію
        const appConfig = configManager.get<IAppConfig>('AppConfig')
        const defaultForgotPasswordUrl = appConfig?.forgotPasswordPageUrl ?? '/'
        const finalForgotPasswordUrl = forgotPasswordPageUrl ?? defaultForgotPasswordUrl

        // Лейбл для поля логіну
        const finalLoginFieldLabel = loginFieldLabel ?? t('Login', { ns: 'Core' })
        
        // Отримуємо значення поля логіну з URL параметра, якщо воно передане
        const urlLoginValue = searchParams.get(loginFieldName) ?? undefined

        /**
         * Обробник відправки форми
         */
        const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
            setIsLoading(true)
            setError(null)

            try {
                // Підготовка даних для відправки
                let loginData = {
                    [loginFieldName]: values[loginFieldName] as string,
                    password: values['password'] as string,
                }

                // Додаємо додаткові поля, якщо вони є
                if (additionalFields) {
                    Object.keys(additionalFields).forEach((key) => {
                        if (values[key] !== undefined) {
                            loginData = { ...loginData, [key]: values[key] }
                        }
                    })
                }

                // Трансформуємо дані, якщо є функція трансформації
                if (transformLoginData) {
                    loginData = transformLoginData(loginData) as { login: string; password: string }
                }

                // Створюємо об'єкт опцій тільки з визначеними значеннями
                const loginOptions: any = {}
                if (authStrategy) {
                    loginOptions.authStrategy = authStrategy
                }
                if (authStrategyOptions) {
                    loginOptions.authStrategyOptions = authStrategyOptions
                }

                // Авторизація через UserManager з передачею стратегії
                const result = await userManager.login(
                    loginData,
                    Object.keys(loginOptions).length > 0 ? loginOptions : undefined
                )

                if (result.success) {
                    // Перевірка ролей, якщо вказані
                    if (requiredRoles.length > 0) {
                        const hasRequiredRole = userManager.hasAnyRole(requiredRoles)

                        if (!hasRequiredRole) {
                            const errorTitle = t('Access denied', { ns: 'Core' })
                            const errorMessage = t('The user does not have access. Please contact the administrator.', {
                                ns: 'Core',
                            })

                            setError(errorMessage)

                            if (onError) {
                                onError(errorTitle, errorMessage)
                            }

                            // Виходимо з системи, якщо немає доступу
                            userManager.logout()
                            setIsLoading(false)
                            return
                        }
                    }

                    // Викликаємо колбек успіху
                    if (onSuccess) {
                        onSuccess()
                    } else {
                        // За замовчуванням перенаправляємо на головну
                        navigate('/')
                    }
                } else {
                    // Обробка помилки авторизації
                    const errorMessage = result.error || t('Unknown error', { ns: 'Core' })
                    setError(errorMessage)

                    if (onError) {
                        const errorTitle = t('Error', { ns: 'Core' })
                        onError(errorTitle, errorMessage)
                    }
                }
            } catch (err) {
                const errorMessage = t('An unknown error has occurred. Please contact the administrator.', {
                    ns: 'Core',
                })
                setError(errorMessage)

                if (onError) {
                    const errorTitle = t('Unknown error', { ns: 'Core' })
                    onError(errorTitle, errorMessage)
                }
            } finally {
                setIsLoading(false)
            }
        }

        // Конфігурація полів форми
        const fields: Record<string, any> = {
            [loginFieldName]: {
                label: finalLoginFieldLabel,
                isRequired: true,
                ...loginFieldProps
            },
            password: {
                label: t('Password', { ns: 'Core' }),
                type: 'password',
                isRequired: true,
                ...passwordFieldProps
            },
        }

        return (
            <div className={styles.formLogin}>
                <Form 
                    onSubmit={handleSubmit} 
                    fields={fields} 
                    initialValues={{
                        [loginFieldName]: urlLoginValue 
                    }}
                >
                    <FormField name={loginFieldName} />
                    <FormField name="password" />

                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.buttons}>
                        <Button className={styles.submitButton} type="primary" htmlType="submit" loading={isLoading}>
                            {t('Log in', { ns: 'Core' })}
                        </Button>

                        {finalForgotPasswordUrl && (
                            <Link to={finalForgotPasswordUrl} className={styles.rememberPasswordLink}>
                                <Button type="link">{t('Forgot password?', { ns: 'Core' })}</Button>
                            </Link>
                        )}
                    </div>
                </Form>
            </div>
        )
    }
)

LoginForm.displayName = 'LoginForm'
